import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm5_u7_a_si';

const images = [
  { key: 'asi_topology', src: '/assets/drawable/asi_topology.png', label: 'AS-i Topologie' }
];

const textBlocks = [
  { key: 'asi_intro', heading: 'AS-Interface (AS-i)', body: 'AS-Interface (AS-i) ist ein einfaches, robustes und kosteneffizientes Feldbussystem für die unterste Feldebene. Es dient der Anbindung von binären Sensoren und Aktoren an eine SPS über eine ungeschirmte zweiadrige Leitung.' },
  { key: 'asi_features', heading: '', body: 'AS-i zeichnet sich durch sehr einfache Installation (Durchdringungstechnik), geringe Verkabelungskosten, robusten Betrieb und schnelle Inbetriebnahme aus.' },
  { key: 'asi_technical', heading: 'Technische Details und Funktionsweise', body: 'Master-Slave-Prinzip, Datenübertragung über aufmoduliertes Signal auf 24V-Busspannung, Zykluszeiten im Millisekundenbereich, bis zu 31/62 adressierbare Slaves je Segment.' },
  { key: 'asi_components', heading: 'Wichtige AS-i-Komponenten', body: '- AS-i-Master: zentrale Steuereinheit.\n- AS-i-Slaves: Sensoren/Aktoren mit AS-i-Schnittstelle.\n- AS-i-Leitung: gelbes zweiadriges Flachkabel (Energie + Daten).' },
  { key: 'asi_advantages', heading: 'Vorteile von AS-i', body: '- Einfache Installation\n- Kosteneffizienz\n- Hohe Robustheit\n- Flexibilität in der Topologie' }
];

const M5_U7_ASi: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM5, canUploadImagesM5 } = useAuth();
  const canEditTexts = canEditTextsM5;
  const canUploadImages = canUploadImagesM5;
  const { darkMode } = useTheme();

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(() => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })), [override]);
  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };
  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token); alert('Gespeichert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, imageKey, file, token); alert('Bild aktualisiert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '5.7 AS-i', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: imgSrc(img.key, img.src), label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, mergedBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m5_u99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M5_U7_ASi;

